import React from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { PiStarFill, PiPlus, PiTrash, PiUserCircle } from "react-icons/pi";
import TestimonialsModern from "./TestimonialsModern";
import TestimonialsSaaS from "./TestimonialsSaaS";
import TestimonialsDark from "./TestimonialsDark";
import TestimonialsCarousel from "./TestimonialsCarousel";
import { EducaeetTestimonials } from "../../educaeet";
import { HokTestimonials } from "../../hok";
import OvanTestimonials from "../../ovan/ovantestimonials/OvanTestimonials";

export default function TestimonialsSection({ content, onContentChange, isSelected }: SectionContentProps) {
    const primaryColor = content.colorPalette?.primary || "#3B82F6";
    const fontFamily = content.fontFamily || "'Inter', sans-serif";

    if (content.variant === "ovan") {
        return <OvanTestimonials content={content} onContentChange={onContentChange} isSelected={isSelected} />;
    }

    if (content.variant === "hok") {
        return <HokTestimonials content={content} onContentChange={onContentChange} isSelected={isSelected} />;
    }

    if (content.variant === 'educaeet') {
        return <EducaeetTestimonials {...{ content, onContentChange, isSelected }} />;
    }

    if (content.variant === 'testimonials-v2') {
        return <TestimonialsDark content={content} onContentChange={onContentChange} isSelected={isSelected} />;
    }

    if (content.variant === 'v3') {
        return <TestimonialsModern content={content} onContentChange={onContentChange} isSelected={isSelected} />;
    }

    if (content.variant === 'v5') {
        return <TestimonialsCarousel content={content} onContentChange={onContentChange} isSelected={isSelected} />;
    }

    if (content.variant === 'v4' || content.variant === 'saas') {
        return <TestimonialsSaaS content={content} onContentChange={onContentChange} isSelected={isSelected} />;
    }

    const testimonials = content.testimonials || [
        { name: "John Doe", role: "CEO, Tech", text: "Amazing platform!" },
        { name: "Jane Smith", role: "Designer", text: "Loved the experience." },
    ];

    const addTestimonial = () => {
        const newItems = [...testimonials, { name: "Name", role: "Role", text: "Feedback text here." }];
        onContentChange?.({ ...content, testimonials: newItems });
    };

    const removeTestimonial = (idx: number) => {
        const newItems = testimonials.filter((_: any, i: number) => i !== idx);
        onContentChange?.({ ...content, testimonials: newItems });
    };

    const updateTestimonial = (idx: number, field: string, value: string) => {
        const newItems = [...testimonials];
        newItems[idx] = { ...newItems[idx], [field]: value };
        onContentChange?.({ ...content, testimonials: newItems });
    };

    return (
        <div
            className={`bg-gray-50 px-8 py-20 ${isSelected ? "ring-2 ring-blue-500" : ""}`}
            style={{ fontFamily }}
        >
            <div className="mx-auto max-w-7xl">
                <h2 className="mb-16 text-center text-4xl font-bold text-gray-900 tracking-tight">
                    <EditableText
                        value={content.title || "What Our Users Say"}
                        onChange={(val) => onContentChange?.({ ...content, title: val })}
                        as="span"
                        placeholder="Section Title"
                    />
                </h2>

                <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                    {testimonials.map((item: any, idx: number) => (
                        <div
                            key={idx}
                            className="group relative flex flex-col rounded-2xl bg-white p-8 shadow-sm transition-all hover:shadow-xl hover:-translate-y-1"
                        >
                            {isSelected && (
                                <button
                                    onClick={(e) => { e.stopPropagation(); removeTestimonial(idx); }}
                                    className="absolute -top-3 -right-3 hidden group-hover:flex h-8 w-8 items-center justify-center rounded-full bg-red-100 text-red-500 shadow-sm"
                                    title="Remove"
                                >
                                    <PiTrash className="h-4 w-4" />
                                </button>
                            )}

                            <div className="mb-6 flex gap-1 text-amber-400">
                                {[1, 2, 3, 4, 5].map((s) => (
                                    <PiStarFill key={s} className="h-5 w-5" />
                                ))}
                            </div>

                            <p className="mb-8 flex-1 text-lg text-gray-600 italic leading-relaxed">
                                "<EditableText
                                    value={item.text}
                                    onChange={(val) => updateTestimonial(idx, "text", val)}
                                    as="span"
                                    placeholder="Testimonial text"
                                />"
                            </p>

                            <div className="flex items-center gap-4 mt-auto">
                                <div className="h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 overflow-hidden">
                                    {item.image || item.avatar ? (
                                        <img src={item.image || item.avatar} alt={item.name} className="h-full w-full object-cover" />
                                    ) : (
                                        <PiUserCircle className="h-8 w-8" />
                                    )}
                                </div>
                                <div>
                                    <div className="font-bold text-gray-900">
                                        <EditableText
                                            value={item.name}
                                            onChange={(val) => updateTestimonial(idx, "name", val)}
                                            as="span"
                                            placeholder="Name"
                                        />
                                    </div>
                                    <div className="text-sm text-gray-500">
                                        <EditableText
                                            value={item.role}
                                            onChange={(val) => updateTestimonial(idx, "role", val)}
                                            as="span"
                                            placeholder="Role"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}

                    {isSelected && (
                        <button
                            onClick={addTestimonial}
                            className="flex min-h-[300px] flex-col items-center justify-center rounded-2xl border-2 border-dashed border-gray-300 bg-white p-8 text-gray-400 hover:border-blue-400 hover:bg-blue-50 hover:text-blue-500 transition-all"
                        >
                            <PiPlus className="h-10 w-10 mb-2" />
                            <span className="text-lg font-medium">Add Testimonial</span>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
